import React, { useState, useEffect, useCallback, useRef } from 'react';
import Sidebar from './components/Sidebar';
import CompassFeed from './components/CompassFeed';
import ChatInterface from './components/ChatInterface';
import BriefingView from './components/BriefingView';

const API = process.env.REACT_APP_API_URL || '';
const SESSION_ID = 'default';

const WELCOME_MESSAGE = {
  role: 'assistant',
  content: "I'm Compass, your VitalChain business advisor. I have full context on your company, pipeline, and the three hard prerequisites blocking pilot outreach.\n\nAsk me anything — CCO pipeline status, insurance next steps, pentest progress, outreach strategy, or what you should be doing right now."
};

export default function App() {
  const [view, setView] = useState('feed');
  const [guidance, setGuidance] = useState(null);
  const [briefing, setBriefing] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Chat state lifted here so it persists across tab switches
  const [chatMessages, setChatMessages] = useState([WELCOME_MESSAGE]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoaded, setChatLoaded] = useState(false);
  const chatDirtyRef = useRef(false); // tracks if new messages were sent since last feed refresh

  // Load chat history from server on mount
  useEffect(() => {
    async function loadChatHistory() {
      try {
        const res = await fetch(`${API}/api/chat/${SESSION_ID}`);
        const data = await res.json();
        if (data.messages && data.messages.length > 0) {
          setChatMessages([WELCOME_MESSAGE, ...data.messages]);
        }
      } catch (err) {
        console.error('Failed to load chat history:', err);
      } finally {
        setChatLoaded(true);
      }
    }
    loadChatHistory();
  }, []);

  const fetchGuidance = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/guidance`);
      const data = await res.json();
      setGuidance(data);
    } catch (err) {
      console.error('Failed to fetch guidance:', err);
    }
  }, []);

  const fetchHealth = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/health`);
      const data = await res.json();
      setHealth(data);
    } catch (err) {
      console.error('Failed to fetch health:', err);
    }
  }, []);

  const fetchBriefing = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/briefing`);
      const data = await res.json();
      setBriefing(data.briefing);
    } catch (err) {
      console.error('Failed to fetch briefing:', err);
    }
  }, []);

  useEffect(() => {
    async function init() {
      setLoading(true);
      await Promise.all([fetchGuidance(), fetchHealth()]);
      setLoading(false);
    }
    init();
  }, [fetchGuidance, fetchHealth]);

  useEffect(() => {
    if (view === 'briefing' && !briefing) {
      fetchBriefing();
    }
  }, [view, briefing, fetchBriefing]);

  // When switching to feed and chat has new messages, refresh guidance
  useEffect(() => {
    if (view === 'feed' && chatDirtyRef.current) {
      chatDirtyRef.current = false;
      fetchGuidance();
    }
  }, [view, fetchGuidance]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetch(`${API}/api/refresh`, { method: 'POST' });
      await Promise.all([fetchGuidance(), fetchHealth()]);
      if (briefing) await fetchBriefing();
    } finally {
      setRefreshing(false);
    }
  };

  // Chat send handler — keeps state in App
  const handleChatSend = async (text, files = []) => {
    // Optimistic local attachments (no filename yet — server assigns one on upload)
    const optimisticAttachments = files.map(f => ({
      originalName: f.name,
      mediaType: f.type,
      kind: f.type.startsWith('image/') ? 'image' : 'document',
      size: f.size
    }));
    const userMsg = { role: 'user', content: text, attachments: optimisticAttachments };
    setChatMessages(prev => [...prev, userMsg]);

    try {
      const formData = new FormData();
      formData.append('message', text);
      formData.append('sessionId', SESSION_ID);
      files.forEach(f => formData.append('files', f));

      const res = await fetch(`${API}/api/chat`, {
        method: 'POST',
        body: formData
      });
      const data = await res.json();

      // Replace optimistic user msg with server's version (has real filenames), then append assistant
      setChatMessages(prev => {
        const next = prev.slice(0, -1);
        next.push({ ...userMsg, attachments: data.attachments || optimisticAttachments });
        next.push({ role: 'assistant', content: data.response || data.error });
        return next;
      });
      chatDirtyRef.current = true;
      return true;
    } catch (err) {
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: `Connection error: ${err.message}. Check that the Compass server is running on port 3001.`
      }]);
      return false;
    }
  };

  const handleChatClear = async () => {
    try {
      await fetch(`${API}/api/chat/${SESSION_ID}`, { method: 'DELETE' });
    } catch { /* ignore */ }
    setChatMessages([WELCOME_MESSAGE]);
    setChatInput('');
    chatDirtyRef.current = true;
  };

  const prereqStatuses = derivePrereqStatuses(guidance?.cards || []);
  const pipelineStats = health?.integrations || {};

  return (
    <div className="app-layout">
      <Sidebar
        prereqStatuses={prereqStatuses}
        integrations={pipelineStats}
        onRefresh={handleRefresh}
        refreshing={refreshing}
        onViewChange={setView}
        currentView={view}
      />
      <div className="main-content">
        <div className="view-tabs">
          <button className={`view-tab ${view === 'feed' ? 'active' : ''}`} onClick={() => setView('feed')}>
            Compass Feed
          </button>
          <button className={`view-tab ${view === 'chat' ? 'active' : ''}`} onClick={() => setView('chat')}>
            Ask Compass
          </button>
          <button className={`view-tab ${view === 'briefing' ? 'active' : ''}`} onClick={() => setView('briefing')}>
            Daily Briefing
          </button>
        </div>
        {view === 'feed' && (
          <CompassFeed cards={guidance?.cards || []} loading={loading} fetchedAt={guidance?.fetchedAt} />
        )}
        {view === 'chat' && (
          <ChatInterface
            messages={chatMessages}
            input={chatInput}
            onInputChange={setChatInput}
            onSend={handleChatSend}
            onClear={handleChatClear}
            loaded={chatLoaded}
          />
        )}
        {view === 'briefing' && (
          <BriefingView briefing={briefing} loading={!briefing} />
        )}
      </div>
    </div>
  );
}

function derivePrereqStatuses(cards) {
  const defaults = [
    { id: 'cco', name: 'CCO Named', status: 'blocked', detail: 'Waiting for data...' },
    { id: 'pentest', name: 'Second Pentest', status: 'blocked', detail: 'Waiting for data...' },
    { id: 'insurance', name: 'Insurance', status: 'blocked', detail: 'Waiting for data...' }
  ];

  if (!cards.length) return defaults;

  return defaults.map(d => {
    const card = cards.find(c => c.prerequisite === d.id || c.category === d.id);
    if (card) {
      let status = 'blocked';
      if (card.title.toLowerCase().includes('complete') || card.title.toLowerCase().includes('confirmed') || card.title.toLowerCase().includes('in place')) {
        status = 'complete';
      } else if (card.title.toLowerCase().includes('follow up') || card.title.toLowerCase().includes('in progress') || card.title.toLowerCase().includes('pending')) {
        status = 'in-progress';
      }
      return { ...d, status, detail: card.summary };
    }
    return d;
  });
}
